import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import moment from "moment";
import dayjs from "dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Avatar,
  Space,
  Checkbox,
  InputNumber,
  Switch,
  Divider,
  Collapse,
  Skeleton,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  DollarOutlined,
  CalendarOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  BellOutlined,
  SettingOutlined,
  MinusCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import { trackEvent } from "../helpers/mixpanel";
import {
  listTenants,
  createTenant,
  updateTenant,
  deleteTenant,
  updateTenantRentDueDay,
  updateTenantRentReminders,
  updateTenantLateNotifications,
} from "../lib/firebase/tenantModel";
import { postTenantMoveOut } from "../lib/api/tenantLedgerApi";
import unitModel from "../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import PageHeader from "../components/PageHeader";
import { StatCard, CardHeader } from "../components/shared";
import { propertyKeys } from "../lib/queryKeys";
import { LAYOUT_CONSTANTS, useBreakpoints } from "../lib/breakpoints";
import { getDebugMode, getDebugUnitCount } from "../helpers/constants";
import { getDebugTenants } from "../helpers/debugData";

const { Option } = Select;
const { TextArea } = Input;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
    max-height: calc(100dvh - 32px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    background: linear-gradient(
      135deg,
      var(--color-primary-darker) 0%,
      var(--color-primary-dark) 100%
    );
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 1.5rem 2rem;
    margin: 0;
  }

  .ant-modal-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
    text-align: center;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 18px;
    top: 16px;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: 36px !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon,
  .ant-modal-close svg,
  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 2rem;
    background: var(--color-bg-secondary);
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
  }

  /* Uniform input/select heights (40px to match taller controls) and proper dropdown arrow centering in Add/Edit Tenant modal */
  .ant-form-item .ant-input:not(textarea),
  .ant-form-item .ant-input-number,
  .ant-form-item .ant-input-affix-wrapper,
  .ant-form-item .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
  }

  .ant-form-item .ant-input-number .ant-input-number-input {
    height: 38px !important;
    min-height: 38px !important;
  }

  /* Affix wrapper: flex alignment so prefix ($) and input align vertically */
  .ant-form-item .ant-input-affix-wrapper {
    display: flex !important;
    align-items: center !important;
  }

  .ant-form-item .ant-input-affix-wrapper .ant-input {
    flex: 1 !important;
    min-height: 0 !important;
    height: 100% !important;
    padding: 0 11px !important;
    line-height: 1.5 !important;
    border: none !important;
  }

  .ant-form-item .ant-input-affix-wrapper .ant-input-prefix {
    display: flex !important;
    align-items: center !important;
  }

  /* Textareas - ensure adequate height, not forced to single-line size */
  .ant-form-item textarea.ant-input {
    min-height: 80px !important;
    height: auto !important;
  }

  .ant-form-item .ant-select {
    height: 40px !important;
  }

  .ant-form-item .ant-select .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    padding-right: 30px !important;
  }

  .ant-form-item .ant-select .ant-select-selection-item,
  .ant-form-item .ant-select .ant-select-selection-placeholder {
    line-height: 1.5 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-form-item .ant-select .ant-select-arrow {
    position: absolute !important;
    right: 11px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

const MoveOutCallout = styled.div`
  margin: 1.25rem 0 0.5rem;
  padding: 1rem 1.1rem;
  border-radius: 10px;
  border: 1px solid #fbbf24;
  background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MoveOutTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: #92400e;
  font-family: var(--font-secondary);
`;

const MoveOutHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #57534e;
  font-family: var(--font-secondary);
`;

const ProcessMoveOutButton = styled(Button)`
  align-self: flex-start;
  border-color: #64748b !important;
  color: #475569 !important;
  background: #f8fafc !important;
  font-weight: 600;
  &:hover {
    border-color: #475569 !important;
    color: #334155 !important;
    background: #f1f5f9 !important;
  }
`;

const Tenants = () => {
  const { user } = useContext(AuthContext);
  const { isMobile } = useBreakpoints();
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [leaseStartDateOpen, setLeaseStartDateOpen] = useState(false);
  const [leaseEndDateOpen, setLeaseEndDateOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [moveOutLoading, setMoveOutLoading] = useState(false);
  const [form] = Form.useForm();

  // Phone number formatting utility
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, "");

    // Don't format if empty or too short
    if (phoneNumber.length === 0) return "";
    if (phoneNumber.length < 4) return phoneNumber;

    // Format based on length
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length < 11) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6)}`;
    } else {
      // Handle longer numbers (with country code)
      return `+${phoneNumber.slice(
        0,
        phoneNumber.length - 10
      )} (${phoneNumber.slice(-10, -7)}) ${phoneNumber.slice(
        -7,
        -4
      )}-${phoneNumber.slice(-4)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    e.target.value = formatted;
    // Update the form field value
    form.setFieldValue("phone", formatted);
  };

  // Load data using useQuery for better caching and performance
  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();
  const debugUnitCount = getDebugUnitCount();

  const { data: tenantsData, isLoading: isTenantsLoading } = useQuery({
    queryKey: propertyKeys.tenants(user?.uid, isDebugMode, debugUnitCount),
    queryFn: () => {
      if (isDebugMode) {
        // Pass unitCount so tenants match the occupied units (85% occupancy)
        return Promise.resolve({ tenants: getDebugTenants(debugUnitCount) });
      }
      return listTenants();
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  const tenants = tenantsData?.tenants || [];
  const loading = isTenantsLoading || isUnitsLoading;

  const showModal = (tenant = null) => {
    trackEvent(tenant ? "tenant_edit_open" : "tenant_create_open", {
      tenantId: tenant?.id,
    });
    // Reset form first
    form.resetFields();

    // Reset date picker states
    setLeaseStartDateOpen(false);
    setLeaseEndDateOpen(false);

    // Reset loading state
    setModalLoading(false);

    // Set tenant data if editing
    setEditingTenant(tenant);

    // Open modal
    setIsModalVisible(true);

    // Use setTimeout to ensure modal is open before setting form values
    setTimeout(() => {
      if (tenant) {
        // Normalize Firestore Timestamp | Date | string to dayjs (for Ant Design DatePicker)
        const normalizeToDayjs = (value) => {
          try {
            if (!value) return null;
            if (typeof value?.toDate === "function") {
              return dayjs(value.toDate());
            }
            if (value?.seconds) {
              return dayjs(new Date(value.seconds * 1000));
            }
            const parsed = new Date(value);
            return isNaN(parsed.getTime()) ? null : dayjs(parsed);
          } catch {
            return null;
          }
        };

        const tenantWithDayjsDates = {
          ...tenant,
          leaseStartDate: normalizeToDayjs(tenant.leaseStartDate),
          leaseEndDate: normalizeToDayjs(tenant.leaseEndDate),
          // Set default values for new fields if they don't exist
          rentDueDay: tenant.rentDueDay || 1,
          rentReminderSettings: tenant.rentReminderSettings || [
            { daysBeforeDue: 7, enabled: true, channel: "email" },
            { daysBeforeDue: 1, enabled: true, channel: "email" },
          ],
          lateRentNotificationSettings: tenant.lateRentNotificationSettings || [
            { daysAfterDue: 1, enabled: true, channel: "email" },
          ],
        };
        form.setFieldsValue(tenantWithDayjsDates);
      } else {
        // Set default values for new tenant
        form.setFieldsValue({
          leaseStatus: "Active",
          unitId: null, // No unit selected by default
          monthlyRent: 0,
          monthToMonthAfterEnd: false,
          rentDueDay: 1,
          rentReminderSettings: [
            { daysBeforeDue: 7, enabled: true, channel: "email" },
            { daysBeforeDue: 1, enabled: true, channel: "email" },
          ],
          lateRentNotificationSettings: [
            { daysAfterDue: 1, enabled: true, channel: "email" },
          ],
        });
      }
    }, 0);
  };

  const handleUnitChange = (unitId) => {
    if (unitId) {
      const selectedUnit = units.find((unit) => unit.id === unitId);
      if (selectedUnit) {
        form.setFieldsValue({
          monthlyRent: selectedUnit.monthlyRent,
        });
      }
    } else {
      // If no unit selected, clear the rent field
      form.setFieldsValue({
        monthlyRent: 0,
      });
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);

      // Convert dayjs objects to Date objects for Firebase (support null end date)
      const toDateOrNull = (val) => {
        if (!val) return null;
        // Handle dayjs objects
        if (val.format && typeof val.format === "function") {
          return val.toDate();
        }
        // Handle moment objects (backward compatibility)
        if (val.toDate && typeof val.toDate === "function") {
          return val.toDate();
        }
        // Handle Firestore Timestamps
        if (typeof val?.toDate === "function") {
          return val.toDate();
        }
        // Handle Date objects and strings
        return val ? new Date(val) : null;
      };

      const processedValues = {
        ...values,
        leaseStartDate: toDateOrNull(values.leaseStartDate),
        leaseEndDate: toDateOrNull(values.leaseEndDate),
        monthToMonthAfterEnd: !!values.monthToMonthAfterEnd,
        emergencyContact: values.emergencyContact || null, // Ensure it's not undefined
        unitId: values.unitId || null, // Allow null unitId
        monthlyRent: Number(values.monthlyRent) || 0, // Ensure it's a number, default to 0
        notes: values.notes || "", // Ensure notes is an empty string if blank
        rentDueDay: Number(values.rentDueDay) || 1, // Ensure rent due day is a number
        rentReminderSettings: values.rentReminderSettings || [
          { daysBeforeDue: 7, enabled: true, channel: "email" },
          { daysBeforeDue: 1, enabled: true, channel: "email" },
        ],
        lateRentNotificationSettings: values.lateRentNotificationSettings || [
          { daysAfterDue: 1, enabled: true, channel: "email" },
        ],
      };

      if (editingTenant) {
        // Update existing tenant in Firebase
        const result = await updateTenant(editingTenant.id, processedValues);
        if (result.success) {
          trackEvent("tenant_update_success", { tenantId: editingTenant.id });
          message.success("Tenant updated successfully!");
        } else {
          trackEvent("tenant_update_error", {
            tenantId: editingTenant.id,
            error: result.error,
          });
        }
      } else {
        // Create new tenant in Firebase
        const result = await createTenant({
          ...processedValues,
          status: "active",
          createdAt: new Date(),
        });
        if (result.success) {
          trackEvent("tenant_create_success", { tenantId: result.tenant?.id });
          message.success("Tenant added successfully!");
        } else {
          trackEvent("tenant_create_error", { error: result.error });
        }
      }

      // Invalidate and refetch tenants data
      await queryClient.invalidateQueries({
        queryKey: propertyKeys.tenants(user?.uid),
      });

      // Close modal and reset state
      setModalLoading(false);
      setIsModalVisible(false);
      setEditingTenant(null);
      setLeaseStartDateOpen(false);
      setLeaseEndDateOpen(false);
      form.resetFields();

      // Force remove any lingering modal overlays (safety check)
      setTimeout(() => {
        const modalMasks = document.querySelectorAll(".ant-modal-mask");
        modalMasks.forEach((mask) => {
          if (mask.style.display !== "none") {
            mask.remove();
          }
        });
      }, 100);
    } catch (error) {
      console.error("Error saving tenant:", error);
      message.error("Failed to save tenant. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalCancel = () => {
    // Force close immediately - ensure state is reset synchronously
    setModalLoading(false);
    setIsModalVisible(false);
    setEditingTenant(null);
    setLeaseStartDateOpen(false);
    setLeaseEndDateOpen(false);
    form.resetFields();

    // Force remove any lingering modal overlays (safety check)
    setTimeout(() => {
      const modalMasks = document.querySelectorAll(".ant-modal-mask");
      modalMasks.forEach((mask) => {
        if (mask.style.display !== "none") {
          mask.remove();
        }
      });
    }, 100);
  };

  const openProcessMoveOutConfirm = () => {
    if (!editingTenant?.id) return;
    const displayName =
      editingTenant.name ||
      [editingTenant.firstName, editingTenant.lastName]
        .filter(Boolean)
        .join(" ") ||
      "this tenant";
    Modal.confirm({
      title: "Process move-out?",
      icon: null,
      content: `This will archive all current messages and prepare the door for a new tenant. This action preserves the legal audit trail for ${displayName}.`,
      okText: "Process move-out",
      cancelText: "Cancel",
      okButtonProps: {
        loading: moveOutLoading,
        style: {
          background: "#d97706",
          borderColor: "#d97706",
          color: "#fff",
        },
      },
      onOk: async () => {
        setMoveOutLoading(true);
        try {
          await postTenantMoveOut(editingTenant.id);
          try {
            if (typeof window !== "undefined") {
              window.localStorage.removeItem(`conv-${editingTenant.id}`);
            }
          } catch (_) {
            /* ignore */
          }
          message.success(
            "Move-out processed. Add a new tenant to this door when you're ready."
          );
          await queryClient.invalidateQueries({
            queryKey: propertyKeys.tenants(
              user?.uid,
              isDebugMode,
              debugUnitCount
            ),
          });
          await queryClient.invalidateQueries({
            queryKey: propertyKeys.units(user?.uid),
          });
          setIsModalVisible(false);
          setEditingTenant(null);
          form.resetFields();
        } catch (e) {
          message.error(e?.message || "Move-out failed");
          throw e;
        } finally {
          setMoveOutLoading(false);
        }
      },
    });
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
  };

  const handleDeleteTenant = (tenantId) => {
    trackEvent("tenant_delete_click", { tenantId });
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "3rem",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ExclamationCircleOutlined
              aria-label="Warning"
              style={{ color: "#000000" }}
            />
          </div>
          <h3
            style={{
              fontFamily: "var(--font-primary)",
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "var(--color-text)",
              margin: "0 0 0.75rem 0",
              lineHeight: "1.4",
            }}
          >
            Delete this tenant?
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete Tenant",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const result = await deleteTenant(tenantId);
          if (result.success) {
            trackEvent("tenant_delete_success", { tenantId });
            message.success("Tenant deleted successfully!");

            // Invalidate and refetch tenants data
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.tenants(user?.uid),
            });
          } else {
            trackEvent("tenant_delete_error", {
              tenantId,
              error: result.error,
            });
            message.error(result.error || "Failed to delete tenant.");
          }
        } catch (error) {
          trackEvent("tenant_delete_exception", {
            tenantId,
            message: String(error),
          });
          console.error("Error deleting tenant:", error);
          message.error("Failed to delete tenant. Please try again.");
        }
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "green";
      case "Inactive":
        return "orange";
      case "Pending":
        return "blue";
      case "Evicted":
        return "red";
      default:
        return "default";
    }
  };

  // Helper function to get ordinal suffix
  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const columns = [
    {
      title: "Tenant",
      dataIndex: "name",
      key: "name",
      width: 250, // Wider column for tenant name and email
      render: (name, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Avatar
            size="large"
            icon={<UserOutlined />}
            style={{
              backgroundColor: "transparent",
              color: "var(--color-primary)",
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0, flex: 1, maxWidth: "100%" }}>
            <Tooltip title={name} placement="top">
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {name}
              </div>
            </Tooltip>
            <Tooltip title={record.email} placement="top">
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "var(--color-text-light)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {record.email}
              </div>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      title: "Door",
      dataIndex: "unitId",
      key: "unitId",
      width: 140, // Narrower unit column
      render: (unitId, record) => {
        const unit = units.find((u) => u.id === unitId);
        const fullUnitName = unit
          ? `${unit.name} - ${getUnitDisplayAddress(unit)}`
          : "Door not found";
        const truncatedName =
          fullUnitName.length > 25
            ? fullUnitName.substring(0, 22) + "..."
            : fullUnitName;

        return (
          <Tooltip title={fullUnitName} placement="top">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <HomeOutlined style={{ color: "var(--color-primary)" }} />
              <span
                style={{
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.9rem",
                }}
              >
                {truncatedName}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Rent",
      dataIndex: "monthlyRent",
      key: "monthlyRent",
      width: 120, // Narrower rent column
      render: (rent) => (
        <div style={{ fontWeight: 600, color: "var(--color-primary)" }}>
          ${rent?.toLocaleString() || "0"}
        </div>
      ),
    },
    {
      title: (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CalendarOutlined style={{ color: "var(--color-primary)" }} />
          Due Day
        </span>
      ),
      dataIndex: "rentDueDay",
      key: "rentDueDay",
      width: 110, // Narrower due day column
      render: (rentDueDay) => (
        <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
          {rentDueDay || 1}
          {getOrdinalSuffix(rentDueDay || 1)}
        </span>
      ),
    },
    {
      title: (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <PhoneOutlined style={{ color: "var(--color-primary)" }} />
          Phone
        </span>
      ),
      dataIndex: "phone",
      key: "phone",
      width: 140, // Narrower phone column
      render: (phone) => {
        const formattedPhone = phone ? formatPhoneNumber(phone) : "N/A";
        return (
          <Tooltip title={formattedPhone} placement="top">
            <span
              style={{
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                display: "block",
              }}
            >
              {formattedPhone}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "leaseStatus",
      key: "leaseStatus",
      width: 110, // Narrower status column
      render: (status) => (
        <Tag
          color={getStatusColor(status)}
          style={{ fontFamily: "var(--font-secondary)" }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CalendarOutlined style={{ color: "var(--color-primary)" }} />
          Start
        </span>
      ),
      dataIndex: "leaseStartDate",
      key: "leaseStartDate",
      width: 120, // Narrower start date column
      render: (date) => {
        const toDate = (val) => {
          try {
            if (!val) return null;
            if (typeof val?.toDate === "function") return val.toDate();
            if (val?.seconds) return new Date(val.seconds * 1000);
            const d = new Date(val);
            return isNaN(d.getTime()) ? null : d;
          } catch {
            return null;
          }
        };
        const d = toDate(date);
        const displayDate = d ? d.toLocaleDateString() : "N/A";
        return (
          <Tooltip title={displayDate} placement="top">
            <span
              style={{
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                display: "block",
              }}
            >
              {displayDate}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CalendarOutlined style={{ color: "var(--color-primary)" }} />
          End
        </span>
      ),
      dataIndex: "leaseEndDate",
      key: "leaseEndDate",
      width: 120, // Narrower end date column
      render: (date, record) => {
        const toDate = (val) => {
          try {
            if (!val) return null;
            if (typeof val?.toDate === "function") return val.toDate();
            if (val?.seconds) return new Date(val.seconds * 1000);
            const d = new Date(val);
            return isNaN(d.getTime()) ? null : d;
          } catch {
            return null;
          }
        };
        const d = toDate(date);
        const base = d ? d.toLocaleDateString() : "N/A";
        const showM2M = !!record?.monthToMonthAfterEnd && !!d;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Tooltip title={base} placement="top">
              <span
                style={{
                  fontSize: "0.9rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: showM2M ? "calc(100% - 20px)" : "100%",
                  flex: 1,
                }}
              >
                {base}
              </span>
            </Tooltip>
            {showM2M && (
              <Tooltip
                title={`After ${base}, this lease converts to month‑to‑month`}
              >
                <InfoCircleOutlined
                  style={{ color: "var(--color-text-light)", flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Button.Group size="small">
          <Tooltip title="Edit Tenant">
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              style={{
                border: "none !important",
                background: "none !important",
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
                color: "var(--color-text)",
              }}
            />
          </Tooltip>
          <Tooltip title="Delete Tenant">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTenant(record.id)}
              style={{
                border: "none !important",
                background: "none !important",
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
                color: "var(--color-text)",
              }}
            />
          </Tooltip>
        </Button.Group>
      ),
    },
  ];

  const totalTenants = tenants.length;
  const activeTenants = tenants.filter(
    (t) => t.leaseStatus === "Active"
  ).length;
  const totalMonthlyRent = tenants.reduce(
    (sum, t) => sum + (t.monthlyRent || 0),
    0
  );

  if (loading) {
    return (
      <Container className="tenants-container">
        <PageHeader
          title="Tenants"
          subtitle="Manage your tenant relationships, leases, and communications"
          icon={UserOutlined}
        />
        <StatsSection>
          <Row gutter={[24, 24]}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} sm={12} lg={8} key={i}>
                <Card>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
        </StatsSection>
        <Card style={{ marginTop: "2rem" }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Container>
    );
  }

  return (
    <Container className="tenants-container">
      <PageHeader
        title="Tenants"
        subtitle="Manage your tenant relationships, leases, and communications"
        icon={UserOutlined}
      />

      {/* Summary Statistics */}
      <StatsSection>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard
              title="Total Tenants"
              value={totalTenants}
              prefix={
                <UserOutlined style={{ color: "var(--color-primary)" }} />
              }
              valueStyle={{ color: "var(--color-primary)" }}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard
              title="Active Leases"
              value={activeTenants}
              prefix={<HomeOutlined style={{ color: "var(--color-accent)" }} />}
              valueStyle={{ color: "var(--color-accent)" }}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard
              title="Monthly Rent Revenue"
              value={totalMonthlyRent}
              prefix={
                <DollarOutlined style={{ color: "var(--color-primary)" }} />
              }
              valueStyle={{ color: "var(--color-primary)" }}
            />
          </Col>
        </Row>
      </StatsSection>

      {/* Tenants Table */}
      <TableSection>
        <Card
          title={`Tenant Directory (${tenants.length})`}
          style={{ borderRadius: "16px", position: "relative" }}
        >
          {isMobile && (
            <Button
              type="primary"
              icon={
                <PlusOutlined
                  style={{ color: "white", filter: "brightness(0) invert(1)" }}
                />
              }
              size="small"
              onClick={() => showModal()}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 10,
                background: "var(--color-primary)",
                borderColor: "var(--color-primary)",
                borderRadius: "8px",
                width: "32px",
                height: "32px",
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          )}
          <AddButton
            type="primary"
            icon={
              <PlusOutlined
                style={{
                  fontSize: 14,
                  marginTop: "8px",
                  filter: "brightness(0) invert(1)",
                }}
              />
            }
            size="small"
            className="add-table-action"
            onClick={() => showModal()}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              zIndex: 10,
              height: "36px",
              padding: "2px 10px",
              fontSize: "13px !important",
              fontFamily: "var(--font-secondary)",
              fontWeight: 600,
              letterSpacing: "0.2px",
              lineHeight: "24px",
              whiteSpace: "nowrap",
              display: isMobile ? "none" : "flex",
            }}
          >
            Add Tenant
          </AddButton>
          {tenants.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <UserOutlined />
              </EmptyIcon>
              <EmptyTitle>No Tenants Yet</EmptyTitle>
              <EmptySubtitle>
                Start building your tenant database by adding your first tenant
              </EmptySubtitle>
              <EmptyActionButton
                type="primary"
                icon={
                  <PlusOutlined style={{ filter: "brightness(0) invert(1)" }} />
                }
                onClick={() => {
                  setEditingTenant(null);
                  setIsModalVisible(true);
                  trackEvent("tenant_create_open", { source: "empty_state" });
                }}
              >
                Add Tenant
              </EmptyActionButton>
            </EmptyState>
          ) : isMobile ? (
            <MobileTenantsCards>
              {tenants.map((tenant) => (
                <TenantCard key={tenant.id}>
                  <MobileCardHeader>
                    <MobileStatusIndicator $status={tenant.leaseStatus}>
                      <UserOutlined
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    </MobileStatusIndicator>
                    <MobileCardTitle>
                      <MobileTenantName>{tenant.name}</MobileTenantName>
                      <MobileTenantEmail>{tenant.email}</MobileTenantEmail>
                    </MobileCardTitle>
                  </MobileCardHeader>

                  <MobileCardContent>
                    <MobileDetailsSection>
                      <MobileDetailRow>
                        <MobileDetailLabel>Door</MobileDetailLabel>
                        <MobileDetailValue
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "right",
                          }}
                        >
                          {getUnitDisplayName(tenant.unitId, units, false)}
                        </MobileDetailValue>
                      </MobileDetailRow>

                      <MobileDetailRow>
                        <MobileDetailLabel>Monthly Rent</MobileDetailLabel>
                        <MobileDetailValue>
                          ${tenant.monthlyRent?.toLocaleString() || "0"}
                        </MobileDetailValue>
                      </MobileDetailRow>

                      <MobileDetailRow>
                        <MobileDetailLabel>Rent Due Day</MobileDetailLabel>
                        <MobileDetailValue>
                          {tenant.rentDueDay || 1}
                          {getOrdinalSuffix(tenant.rentDueDay || 1)}
                        </MobileDetailValue>
                      </MobileDetailRow>

                      <MobileDetailRow>
                        <MobileDetailLabel>Lease Status</MobileDetailLabel>
                        <MobileStatusTag
                          color={getStatusColor(tenant.leaseStatus)}
                        >
                          {tenant.leaseStatus}
                        </MobileStatusTag>
                      </MobileDetailRow>

                      <MobileDetailRow>
                        <MobileDetailLabel>Lease Start</MobileDetailLabel>
                        <MobileDetailValue>
                          {formatDate(tenant.leaseStartDate)}
                        </MobileDetailValue>
                      </MobileDetailRow>

                      <MobileDetailRow>
                        <MobileDetailLabel>Lease End</MobileDetailLabel>
                        <MobileDetailValue>
                          {formatDate(tenant.leaseEndDate)}
                        </MobileDetailValue>
                      </MobileDetailRow>
                    </MobileDetailsSection>

                    <MobileActionButtons>
                      <MobileActionButton
                        type="default"
                        icon={
                          <EditOutlined
                            style={{
                              color: "white",
                              filter: "brightness(0) invert(1)",
                            }}
                          />
                        }
                        onClick={() => showModal(tenant)}
                      />
                      <MobileActionButton
                        type="default"
                        icon={
                          <DeleteOutlined
                            style={{
                              color: "white",
                              filter: "brightness(0) invert(1)",
                            }}
                          />
                        }
                        onClick={() => handleDeleteTenant(tenant.id)}
                        danger
                      />
                    </MobileActionButtons>
                  </MobileCardContent>
                </TenantCard>
              ))}
            </MobileTenantsCards>
          ) : (
            <Table
              columns={columns}
              dataSource={tenants}
              rowKey="id"
              scroll={{ x: 1230 }}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} tenants`,
                onShowSizeChange: handlePageSizeChange,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              loading={loading}
            />
          )}
        </Card>
      </TableSection>

      {/* Add/Edit Tenant Modal */}
      <StyledModal
        title={editingTenant ? "Edit Tenant" : "Add New Tenant"}
        open={isModalVisible}
        centered
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        afterClose={() => {
          // Ensure state is completely reset after modal closes (animation completes)
          setEditingTenant(null);
          setLeaseStartDateOpen(false);
          setLeaseEndDateOpen(false);
          setModalLoading(false);
          form.resetFields();
        }}
        confirmLoading={modalLoading}
        width={isMobile ? 600 : 750}
        okText={editingTenant ? "Update" : "Add"}
        cancelText="Cancel"
        destroyOnClose={true}
        maskClosable={true}
        styles={{
          body: {
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "20px",
            paddingBottom: "0",
            maxWidth: "100%",
          },
          footer: {
            paddingTop: "1.5rem",
            paddingBottom: "0",
          },
        }}
        style={{
          maxWidth: isMobile ? "95vw" : "90vw",
        }}
        className="tenant-edit-modal tenant-edit-modal-responsive"
        wrapProps={{
          style: {
            overflowX: "hidden",
          },
        }}
      >
        <MobileLabelStyles>
          <Form
            form={form}
            layout="vertical"
            style={{
              overflowX: "hidden",
              maxWidth: "100%",
              width: "100%",
              boxSizing: "border-box",
            }}
            className="tenant-form"
            initialValues={{
              leaseStatus: "Active",
              unitId: null,
              monthlyRent: 0,
              monthToMonthAfterEnd: false,
              rentDueDay: 1,
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="name"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <UserOutlined style={{ color: "var(--color-primary)" }} />
                      <span className="desktop-label">Full Name</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter tenant name" },
                  ]}
                >
                  <Input placeholder="e.g., John Smith" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="email"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <MailOutlined style={{ color: "var(--color-primary)" }} />
                      <span className="desktop-label">Email Address</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter email address" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="john.smith@email.com" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="phone"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <PhoneOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span className="desktop-label">Phone Number</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter phone number" },
                    {
                      pattern: /^\(\d{3}\) \d{3}-\d{4}$/,
                      message: "Please enter a valid phone number",
                    },
                  ]}
                >
                  <Input
                    placeholder="(555) 123-4567"
                    onChange={handlePhoneChange}
                    maxLength={14}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="emergencyContact"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <ExclamationCircleOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span className="desktop-label">Emergency Contact</span>
                    </span>
                  }
                >
                  <Input placeholder="Emergency contact name and phone" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="unitId"
              label={
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <HomeOutlined style={{ color: "var(--color-primary)" }} />
                  <span className="desktop-label">Door (Optional)</span>
                </span>
              }
              extra="Tenants must have a door assigned to receive payment emails and reminders."
            >
              <Select
                placeholder="Select a door (optional)"
                onChange={handleUnitChange}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {units.length > 0 ? (
                  units.map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name} - {getUnitDisplayAddress(unit)}
                    </Option>
                  ))
                ) : (
                  <Option value="" disabled>
                    No doors available. You can add doors later.
                  </Option>
                )}
              </Select>
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="monthlyRent"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <DollarOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span className="desktop-label">
                        Monthly Rent (Optional)
                      </span>
                    </span>
                  }
                >
                  <Input type="number" prefix="$" placeholder="0" min={0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="leaseStatus"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <CheckCircleOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span className="desktop-label">Lease Status</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please select lease status" },
                  ]}
                >
                  <Select>
                    <Option value="Active">Active</Option>
                    <Option value="Pending">Pending</Option>
                    <Option value="Inactive">Inactive</Option>
                    <Option value="Evicted">Evicted</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="leaseStartDate"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <CalendarOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span className="desktop-label">Lease Start Date</span>
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select lease start date",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Select start date"
                    format="MM/DD/YYYY"
                    allowClear={true}
                    picker="date"
                    showToday={true}
                    showTime={false}
                    open={leaseStartDateOpen}
                    onOpenChange={(open) => {
                      setLeaseStartDateOpen(open);
                    }}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    onCalendarChange={(date, dateString, info) => {
                      // Only allow date changes when explicitly selecting a date
                      // Ignore changes from year/month panel navigation
                      if (info?.source === "date" && date) {
                        // Date was explicitly selected, allow it
                        return;
                      }
                      // Prevent any other changes (like panel navigation)
                      if (info?.source !== "date") {
                        // This is panel navigation, don't update the form
                        const currentValue =
                          form.getFieldValue("leaseStartDate");
                        if (
                          currentValue &&
                          date &&
                          !date.isSame(currentValue, "day")
                        ) {
                          // Date changed from navigation, reset it
                          form.setFieldValue("leaseStartDate", currentValue);
                        }
                      }
                    }}
                    onChange={(date, dateString) => {
                      // Close picker after selection
                      if (date) {
                        setLeaseStartDateOpen(false);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="leaseEndDate"
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <CalendarOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span className="desktop-label">Lease End Date</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please select lease end date" },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Select end date"
                    format="MM/DD/YYYY"
                    allowClear={false}
                    picker="date"
                    showToday={true}
                    showTime={false}
                    open={leaseEndDateOpen}
                    onOpenChange={(open) => {
                      setLeaseEndDateOpen(open);
                    }}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    onCalendarChange={(date, dateString, info) => {
                      // Only allow date changes when explicitly selecting a date
                      // Ignore changes from year/month panel navigation
                      if (info?.source === "date" && date) {
                        // Date was explicitly selected, allow it
                        return;
                      }
                      // Prevent any other changes (like panel navigation)
                      if (info?.source !== "date") {
                        // This is panel navigation, don't update the form
                        const currentValue = form.getFieldValue("leaseEndDate");
                        if (
                          currentValue &&
                          date &&
                          !date.isSame(currentValue, "day")
                        ) {
                          // Date changed from navigation, reset it
                          form.setFieldValue("leaseEndDate", currentValue);
                        }
                      }
                    }}
                    onChange={(date, dateString) => {
                      // Close picker after selection
                      if (date) {
                        setLeaseEndDateOpen(false);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="monthToMonthAfterEnd"
                  valuePropName="checked"
                  style={{ marginTop: -6 }}
                >
                  <Checkbox>
                    <span
                      style={{
                        fontSize: "0.9rem",
                        color: "var(--color-text)",
                        lineHeight: "1.4",
                        fontFamily: "var(--font-secondary)",
                        fontWeight: 500,
                        whiteSpace: "normal",
                      }}
                    >
                      After end date, convert to
                      {isMobile && <br />}
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          lineHeight: "1.4",
                          fontFamily: "var(--font-secondary)",
                          color: "var(--color-primary)",
                          whiteSpace: "normal",
                        }}
                      >
                        month-to-month
                      </span>
                    </span>
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="notes"
              label={
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <FileTextOutlined style={{ color: "var(--color-primary)" }} />
                  <span className="desktop-label">Additional Notes</span>
                </span>
              }
            >
              <TextArea
                rows={3}
                placeholder="Any additional information about the tenant..."
              />
            </Form.Item>

            <Divider>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <BellOutlined style={{ color: "var(--color-primary)" }} />
                <span
                  style={{
                    fontFamily: "var(--font-secondary)",
                    fontWeight: 600,
                  }}
                >
                  Rent & Notification Settings
                </span>
              </div>
            </Divider>

            <SettingsSection>
              <Row gutter={16}>
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="rentDueDay"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <ClockCircleOutlined
                          style={{ color: "var(--color-primary)" }}
                        />
                        <span className="desktop-label">Rent Due Day</span>
                      </span>
                    }
                    labelCol={{ xs: 24, sm: 24, md: 8, lg: 7, xl: 6 }}
                    wrapperCol={{ xs: 24, sm: 24, md: 16, lg: 17, xl: 18 }}
                    rules={[
                      { required: true, message: "Please select rent due day" },
                      {
                        type: "number",
                        min: 1,
                        max: 31,
                        message: "Must be between 1 and 31",
                      },
                    ]}
                    className="rent-due-day-horizontal"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Day of month (1-31)"
                      min={1}
                      max={31}
                      formatter={(value) =>
                        `${value}${getOrdinalSuffix(value)}`
                      }
                      parser={(value) => value.replace(/[^\d]/g, "")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </SettingsSection>

            <Collapse
              ghost
              style={{ marginBottom: "1rem" }}
              items={[
                {
                  key: "reminders",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <MessageOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span
                        style={{
                          fontFamily: "var(--font-secondary)",
                          fontWeight: 600,
                        }}
                      >
                        Rent Reminder Settings
                      </span>
                    </div>
                  ),
                  children: (
                    <SettingsSection>
                      <Form.List name="rentReminderSettings">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <ReminderFieldGroup key={key}>
                                <div className="reminder-row">
                                  <div className="mobile-top-row">
                                    <div className="enabled-group">
                                      <Form.Item
                                        {...restField}
                                        name={[name, "enabled"]}
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Switch />
                                      </Form.Item>
                                    </div>
                                    <div className="remove-group">
                                      <div
                                        onClick={() => remove(name)}
                                        title="Remove reminder"
                                        style={{
                                          width: "44px",
                                          height: "44px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "28px",
                                          fontWeight: "bold",
                                          color: "#000000",
                                          cursor: "pointer",
                                          backgroundColor: "transparent",
                                          border: "none",
                                          borderRadius: "4px",
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.target.style.backgroundColor =
                                            "rgba(0,0,0,0.05)")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.target.style.backgroundColor =
                                            "transparent")
                                        }
                                      >
                                        ×
                                      </div>
                                    </div>
                                  </div>
                                  <div className="field-group">
                                    <label>Days Before Due</label>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "daysBeforeDue"]}
                                      rules={[
                                        { required: true, message: "Required" },
                                        {
                                          type: "number",
                                          min: 1,
                                          max: 30,
                                          message: "1-30 days",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <InputNumber
                                        style={{ width: "100%" }}
                                        placeholder="Days"
                                        min={1}
                                        max={30}
                                      />
                                    </Form.Item>
                                  </div>
                                  <div className="field-group">
                                    <label>Channel</label>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "channel"]}
                                      rules={[
                                        { required: true, message: "Required" },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Select
                                        style={{ width: "100%", marginTop: 0 }}
                                      >
                                        <Option value="email">Email</Option>
                                        <Option value="sms" disabled>
                                          <span
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <LockOutlined
                                              style={{
                                                fontSize: "12px",
                                                color: "#999",
                                              }}
                                            />
                                            <span>SMS</span>
                                          </span>
                                        </Option>
                                        <Option value="both" disabled>
                                          <span
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <LockOutlined
                                              style={{
                                                fontSize: "12px",
                                                color: "#999",
                                              }}
                                            />
                                            <span>Both</span>
                                          </span>
                                        </Option>
                                      </Select>
                                    </Form.Item>
                                  </div>
                                  <div className="enabled-group">
                                    <label>Enabled</label>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "enabled"]}
                                      valuePropName="checked"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Switch />
                                    </Form.Item>
                                  </div>
                                  <div className="remove-group">
                                    <div
                                      onClick={() => remove(name)}
                                      title="Remove reminder"
                                      style={{
                                        width: "36px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "#000000",
                                        cursor: "pointer",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        borderRadius: "4px",
                                        marginTop: "16px",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor =
                                          "rgba(0,0,0,0.05)")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      ×
                                    </div>
                                  </div>
                                </div>
                              </ReminderFieldGroup>
                            ))}
                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() =>
                                  add({
                                    daysBeforeDue: 7,
                                    enabled: true,
                                    channel: "email",
                                  })
                                }
                                block
                                icon={
                                  <PlusOutlined style={{ color: "#000000" }} />
                                }
                                style={{ color: "#000000" }}
                                className="add-reminder-button"
                              >
                                <span style={{ color: "#000000" }}>
                                  Add Reminder
                                </span>
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </SettingsSection>
                  ),
                },
                {
                  key: "lateNotifications",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                      <span
                        style={{
                          fontFamily: "var(--font-secondary)",
                          fontWeight: 600,
                        }}
                      >
                        Late Rent Notification Settings
                      </span>
                    </div>
                  ),
                  children: (
                    <SettingsSection>
                      <Form.List name="lateRentNotificationSettings">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <ReminderFieldGroup key={key}>
                                <div className="reminder-row">
                                  <div className="mobile-top-row">
                                    <div className="enabled-group">
                                      <Form.Item
                                        {...restField}
                                        name={[name, "enabled"]}
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Switch />
                                      </Form.Item>
                                    </div>
                                    <div className="remove-group">
                                      <div
                                        onClick={() => remove(name)}
                                        title="Remove notification"
                                        style={{
                                          width: "44px",
                                          height: "44px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "28px",
                                          fontWeight: "bold",
                                          color: "#000000",
                                          cursor: "pointer",
                                          backgroundColor: "transparent",
                                          border: "none",
                                          borderRadius: "4px",
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.target.style.backgroundColor =
                                            "rgba(0,0,0,0.05)")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.target.style.backgroundColor =
                                            "transparent")
                                        }
                                      >
                                        ×
                                      </div>
                                    </div>
                                  </div>
                                  <div className="field-group">
                                    <label>Days After Due</label>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "daysAfterDue"]}
                                      rules={[
                                        { required: true, message: "Required" },
                                        {
                                          type: "number",
                                          min: 1,
                                          max: 30,
                                          message: "1-30 days",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <InputNumber
                                        style={{ width: "100%" }}
                                        placeholder="Days"
                                        min={1}
                                        max={30}
                                      />
                                    </Form.Item>
                                  </div>
                                  <div className="field-group">
                                    <label>Channel</label>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "channel"]}
                                      rules={[
                                        { required: true, message: "Required" },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Select
                                        style={{ width: "100%", marginTop: 0 }}
                                      >
                                        <Option value="email">Email</Option>
                                        <Option value="sms" disabled>
                                          <span
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <LockOutlined
                                              style={{
                                                fontSize: "12px",
                                                color: "#999",
                                              }}
                                            />
                                            <span>SMS</span>
                                          </span>
                                        </Option>
                                        <Option value="both" disabled>
                                          <span
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <LockOutlined
                                              style={{
                                                fontSize: "12px",
                                                color: "#999",
                                              }}
                                            />
                                            <span>Both</span>
                                          </span>
                                        </Option>
                                      </Select>
                                    </Form.Item>
                                  </div>
                                  <div className="enabled-group">
                                    <label>Enabled</label>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "enabled"]}
                                      valuePropName="checked"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Switch />
                                    </Form.Item>
                                  </div>
                                  <div className="remove-group">
                                    <div
                                      onClick={() => remove(name)}
                                      title="Remove notification"
                                      style={{
                                        width: "36px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "#000000",
                                        cursor: "pointer",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        borderRadius: "4px",
                                        marginTop: "16px",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor =
                                          "rgba(0,0,0,0.05)")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      ×
                                    </div>
                                  </div>
                                </div>
                              </ReminderFieldGroup>
                            ))}
                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() =>
                                  add({
                                    daysAfterDue: 1,
                                    enabled: true,
                                    channel: "email",
                                  })
                                }
                                block
                                icon={
                                  <PlusOutlined style={{ color: "#000000" }} />
                                }
                                style={{ color: "#000000" }}
                                className="add-notification-button"
                              >
                                <span style={{ color: "#000000" }}>
                                  Add Late Notification
                                </span>
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </SettingsSection>
                  ),
                },
              ]}
            />

            {editingTenant &&
              String(editingTenant.status || "").toLowerCase() !==
                "moved_out" && (
                <MoveOutCallout>
                  <MoveOutTitle>Tenant turnover</MoveOutTitle>
                  <MoveOutHint>
                    When someone moves out, process move-out to archive the
                    landlord-tenant message thread (audit trail kept), clear this
                    door for the next occupant, and end active magic-link chat
                    sessions for the previous household.
                  </MoveOutHint>
                  <ProcessMoveOutButton
                    loading={moveOutLoading}
                    onClick={openProcessMoveOutConfirm}
                  >
                    Process move-out
                  </ProcessMoveOutButton>
                </MoveOutCallout>
              )}

            {!editingTenant && (
              <>
                <div
                  style={{
                    marginTop: "24px",
                    padding: "16px",
                    backgroundColor: "#f0f7ff",
                    borderRadius: "8px",
                    border: "1px solid #b3d9ff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}
                  >
                    <MailOutlined
                      style={{
                        color: "var(--color-primary)",
                        fontSize: "18px",
                        marginTop: "2px",
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1e293b",
                          marginBottom: "4px",
                          fontSize: "14px",
                        }}
                      >
                        Welcome Email
                      </div>
                      <div
                        style={{
                          color: "#475569",
                          fontSize: "13px",
                          lineHeight: "1.5",
                        }}
                      >
                        New tenants with an assigned door and email address will
                        receive a welcome email within 24 hours of being added.
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "16px",
                    marginBottom: "16px",
                    padding: "16px",
                    backgroundColor: "#f0fdf4",
                    borderRadius: "8px",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}
                  >
                    <DollarOutlined
                      style={{
                        color: "var(--color-primary)",
                        fontSize: "18px",
                        marginTop: "2px",
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1e293b",
                          marginBottom: "4px",
                          fontSize: "14px",
                        }}
                      >
                        Automatic Payment Creation
                      </div>
                      <div
                        style={{
                          color: "#475569",
                          fontSize: "13px",
                          lineHeight: "1.5",
                        }}
                      >
                        Rent payment records are automatically created by the
                        system within 24 hours when a tenant has an assigned
                        door, monthly rent amount, and rent due day set. These
                        are placeholders for upcoming rent payments that can be
                        viewed and tracked in the "Rent" tab.
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Form>
        </MobileLabelStyles>
      </StyledModal>
    </Container>
  );
};

export default Tenants;

// Helper function to get ordinal suffix for rent due day
const getOrdinalSuffix = (num) => {
  if (num > 3 && num < 21) return "th";
  switch (num % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Helper function to format dates
const formatDate = (date) => {
  const toDate = (val) => {
    try {
      if (!val) return null;
      if (typeof val?.toDate === "function") return val.toDate();
      if (val?.seconds) return new Date(val.seconds * 1000);
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };
  const d = toDate(date);
  return d ? d.toLocaleDateString() : "N/A";
};

// Helper function to get unit display name
const getUnitDisplayName = (unitId, units, truncate = false) => {
  const unit = units.find((u) => u.id === unitId);
  if (!unit) return "Door not found";
  const fullUnitName = `${unit.name} - ${getUnitDisplayAddress(unit)}`;
  // Only truncate if explicitly requested (for desktop table view)
  if (truncate && fullUnitName.length > 25) {
    return fullUnitName.substring(0, 22) + "...";
  }
  return fullUnitName;
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "#52c41a";
    case "Inactive":
      return "#f5222d";
    case "Pending":
      return "#faad14";
    default:
      return "#d9d9d9";
  }
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
    overflow-x: hidden;
    max-width: 100vw;
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }

  /* Apply the new container class for better background */
  &.tenants-container {
    background: var(--color-bg-primary) !important;
  }

  /* Force modal labels to be black on mobile - ULTRA HIGH SPECIFICITY */
  .tenant-edit-modal .ant-form-item-label > label,
  .ant-modal.tenant-edit-modal .ant-form-item-label > label,
  .ant-modal .ant-form-item-label > label {
    color: #000000 !important;

    @media (max-width: 768px) {
      color: #000000 !important;

      &,
      * {
        color: #000000 !important;
      }

      span {
        color: #000000 !important;
      }

      .anticon {
        color: var(--color-primary) !important;
      }
    }

    span {
      color: #000000 !important;
    }

    .anticon {
      color: var(--color-primary) !important;
    }
  }

  /* Select dropdown arrow vertical centering */
  .ant-select {
    position: relative !important;

    .ant-select-selector {
      position: relative !important;
      display: flex !important;
      align-items: center !important;
      padding-right: 30px !important;
      min-height: 32px !important;
      height: 32px !important;
      max-height: 32px !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      display: flex !important;
      align-items: center !important;
      height: 100% !important;
      font-size: 14px !important;
    }

    .ant-select-arrow {
      position: absolute !important;
      right: 11px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin-top: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      margin-bottom: 0 !important;
      color: var(--color-text) !important;
      font-size: 12px !important;
      line-height: 1 !important;
      height: auto !important;
      width: auto !important;
      filter: brightness(1) invert(0) !important;
      pointer-events: none !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      &:hover {
        filter: brightness(1) invert(0) !important;
      }

      .anticon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
        vertical-align: middle !important;
      }

      svg {
        vertical-align: middle !important;
        display: inline-block !important;
      }
    }
  }

  /* Ensure the arrow container is properly sized */
  .ant-select-single .ant-select-selector {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    font-size: 14px !important;
    line-height: 1.5 !important;
    height: auto !important;
  }

  /* Style disabled Select options - same size as regular options */
  .ant-select-dropdown .ant-select-item-option-disabled {
    opacity: 0.6 !important;
    color: #999 !important;
    cursor: not-allowed !important;
    background-color: #f5f5f5 !important;
    font-size: 14px !important;
  }

  .ant-select-dropdown .ant-select-item-option-disabled:hover {
    background-color: #f5f5f5 !important;
  }

  .ant-select-dropdown .ant-select-item-option-disabled .anticon {
    color: #999 !important;
  }

  .ant-select-dropdown .ant-select-item-option-disabled span {
    font-size: 14px !important;
  }

  /* Ensure all dropdown options use 14px font size */
  .ant-select-dropdown .ant-select-item-option {
    font-size: 14px !important;
  }

  .ant-select-dropdown .ant-select-item-option-content {
    font-size: 14px !important;
  }

  .ant-select-dropdown .ant-select-item-option span {
    font-size: 14px !important;
  }

  /* Ensure dashed buttons have black text - override global white text rule */
  .ant-btn-dashed,
  .ant-btn-dashed span,
  .ant-btn-dashed > span,
  .ant-btn-dashed > *,
  .add-reminder-button,
  .add-reminder-button span,
  .add-reminder-button > span,
  .add-reminder-button > *,
  .add-notification-button,
  .add-notification-button span,
  .add-notification-button > span,
  .add-notification-button > * {
    color: #000000 !important;
  }

  .ant-btn-dashed:hover,
  .ant-btn-dashed:hover span,
  .ant-btn-dashed:hover > span,
  .ant-btn-dashed:hover > *,
  .add-reminder-button:hover,
  .add-reminder-button:hover span,
  .add-reminder-button:hover > span,
  .add-reminder-button:hover > *,
  .add-notification-button:hover,
  .add-notification-button:hover span,
  .add-notification-button:hover > span,
  .add-notification-button:hover > * {
    color: #000000 !important;
  }

  .ant-btn-dashed:focus,
  .ant-btn-dashed:focus span,
  .ant-btn-dashed:focus > span,
  .ant-btn-dashed:focus > *,
  .add-reminder-button:focus,
  .add-reminder-button:focus span,
  .add-reminder-button:focus > span,
  .add-reminder-button:focus > *,
  .add-notification-button:focus,
  .add-notification-button:focus span,
  .add-notification-button:focus > span,
  .add-notification-button:focus > * {
    color: #000000 !important;
  }

  .ant-btn-dashed .anticon,
  .ant-btn-dashed span .anticon,
  .add-reminder-button .anticon,
  .add-reminder-button span .anticon,
  .add-notification-button .anticon,
  .add-notification-button span .anticon {
    color: #000000 !important;
  }
`;

const AddButton = styled(Button)`
  background: var(--color-primary) !important;
  border: 2px solid var(--color-primary) !important;
  color: white !important;
  border-radius: 16px;
  height: 48px;
  font-size: 1rem !important;
  font-weight: 700;
  font-family: var(--font-secondary);
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.2);
  padding: 0 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;

  .ant-btn-icon {
    filter: brightness(0) invert(1) !important;
  }

  &:hover:not(:disabled) {
    background: var(--color-primary-dark) !important;
    border-color: var(--color-primary-dark) !important;
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const StatsSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  .ant-card {
    --stat-title-size: 0.875rem;
    --stat-value-size: 1.5rem;
    --stat-suffix-size: 1.5rem;
    --stat-icon-size: 1.5rem;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: var(--stat-title-size) !important;
    line-height: 1.25;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content,
  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700 !important;
  }

  .ant-statistic-content-suffix,
  .ant-statistic-content-prefix {
    font-size: var(--stat-suffix-size) !important;
    font-weight: 700 !important;
    line-height: 1.2;
  }

  .ant-statistic-content-prefix svg,
  .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
    width: var(--stat-icon-size) !important;
    height: var(--stat-icon-size) !important;
  }

  @media (max-width: 768px) {
    .ant-card {
      --stat-title-size: 0.8rem;
      --stat-value-size: 1.25rem;
      --stat-suffix-size: 1.25rem;
      --stat-icon-size: 1.25rem;
    }
  }
`;

const TableSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  border-radius: 0 !important;

  /* Enhanced horizontal scrolling */
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: var(--color-primary) transparent;

  /* Custom scrollbar styling for webkit browsers */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary-dark);
  }

  .ant-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
  }

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    padding: 0;
  }

  .ant-card-head-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 1.25rem;
    padding: 1.5rem 2rem 1rem 2rem;
  }

  .ant-table {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
    table-layout: fixed; /* Use fixed layout for better column control */
  }

  /* Ensure scrollbar is visible when table can scroll */
  .ant-table-container {
    /* Use scroll instead of auto to force scrollbar visibility when content can scroll */
    overflow-y: scroll !important;
    overflow-x: auto !important;
  }

  /* Custom scrollbar styling for table body - always visible track */
  .ant-table-body {
    scrollbar-width: thin !important;
    scrollbar-color: var(--color-primary) rgba(0, 0, 0, 0.1) !important;
    /* Force scrollbar to always be visible when content can scroll */
    overflow-y: scroll !important;
  }

  .ant-table-body::-webkit-scrollbar {
    width: 8px !important;
    display: block !important; /* Always show scrollbar */
    -webkit-appearance: none !important;
  }

  .ant-table-body::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05) !important;
    border-radius: 4px !important;
    display: block !important; /* Always show track */
  }

  .ant-table-body::-webkit-scrollbar-thumb {
    background: var(--color-primary) !important;
    border-radius: 4px !important;
    opacity: 0.7 !important; /* Make it more visible */
  }

  .ant-table-body::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary-dark) !important;
    opacity: 1 !important;
  }

  /* Table header styling */
  .ant-table-thead > tr > th {
    background: var(--color-bg-tertiary);
    border-bottom: 2px solid var(--color-border-light);
    font-family: var(--font-secondary);
    font-weight: 600;
    color: #ffffff;
    padding: 1.25rem 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-tbody > tr > td {
    padding: 1.25rem 1rem;
    border-bottom: 1px solid var(--color-border-light);
    font-family: var(--font-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    vertical-align: middle;
  }

  .ant-card-body {
    box-sizing: border-box;
  }

  /* Let the desktop table use the card's full horizontal width. */
  @media (min-width: 769px) {
    .ant-card-body {
      padding-top: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }

  /* Fix pagination alignment */
  .ant-pagination {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .ant-pagination-total-text {
    display: flex;
    align-items: center;
    height: 32px;
    line-height: 32px;
  }

  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 32px;
    line-height: 32px;
  }

  .ant-pagination-options {
    display: flex;
    align-items: center;
    height: 32px;
    margin-left: 0;
    position: relative;
    z-index: 1;

    .ant-select {
      display: flex;
      align-items: center;
      height: 32px;
      position: relative;
      z-index: 100;
    }

    .ant-select-selector {
      display: flex;
      align-items: center;
      height: 32px !important;
      padding: 0 11px;
    }

    .ant-select-selection-item {
      display: flex;
      align-items: center;
      line-height: 32px;
      height: 32px;
    }

    .ant-select-arrow {
      display: flex;
      align-items: center;
      top: 50%;
      transform: translateY(-50%);
      margin-top: 0 !important;
    }
  }

  /* Prevent table scroll when pagination dropdown is open */
  .ant-table-pagination {
    position: relative;
    z-index: 10;
  }

  /* Prevent table body from scrolling when pagination dropdown opens */
  .ant-table-body {
    scroll-behavior: auto !important;
    overflow-anchor: none !important;
  }

  /* Prevent scroll on table container */
  .ant-table-container {
    scroll-behavior: auto !important;
    overflow-anchor: none !important;
  }

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    padding: 0;

    .ant-card {
      margin: 0;
      border-radius: 12px;
    }

    .ant-card-body {
      padding: 1rem;
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 0.75rem 0.75rem;
    }
  }
`;

// Mobile Card Components
const MobileTenantsCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;
`;

const TenantCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 100%
    );
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const MobileStatusIndicator = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  color: white;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const MobileCardTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MobileTenantName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
`;

const MobileTenantEmail = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const MobileCardContent = styled.div`
  padding: 1rem;
`;

const MobileDetailsSection = styled.div`
  margin-bottom: 1.5rem;
`;

const MobileDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileDetailLabel = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
  font-weight: 500;
`;

const MobileDetailValue = styled.div`
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: 600;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
  min-width: 0; /* Allow flex item to shrink below content size */
`;

const MobileStatusTag = styled(Tag)`
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border: none;
`;

const MobileActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MobileActionButton = styled(Button)`
  flex: 1;
  min-width: 0;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);
    &:hover {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
    }
    .anticon {
      color: white !important;
    }
  }

  &.ant-btn-default {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
    &:hover {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
      color: white;
    }
    .anticon {
      color: white !important;
    }
    &:hover .anticon {
      color: white !important;
    }
  }

  &.ant-btn-dangerous {
    background: #ff4d4f;
    border-color: #ff4d4f;
    color: white;
    &:hover {
      background: #ff7875;
      border-color: #ff7875;
    }
    .anticon {
      color: white !important;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
`;

const EmptyIcon = styled.div`
  font-size: 2.98rem;
  margin-bottom: 1rem;
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 2.98rem !important;
  }

  .anticon svg {
    width: 2.98rem !important;
    height: 2.98rem !important;
  }

  @media (max-width: 768px) {
    font-size: 2.34rem;

    .anticon {
      font-size: 2.34rem !important;
    }

    .anticon svg {
      width: 2.34rem !important;
      height: 2.34rem !important;
    }
  }
`;

const EmptyTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.5rem 0;
`;

const EmptySubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: var(--color-text-light);
  margin: 0 0 2rem 0;
`;

const EmptyActionButton = styled(Button)`
  margin-top: 1rem;
  height: 44px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 0 2rem;
`;

const AddFirstTenantButton = styled(Button)`
  background: var(--color-primary);
  border: none;
  color: white !important;
  border-radius: 12px;
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  padding: 0 2rem;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(44, 95, 45, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-primary-dark);
    color: white !important;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: translateY(0);
    color: white !important;
  }
`;

// Mobile responsive labels - show text on mobile
const MobileLabelStyles = styled.div`
  /* Keep desktop-label visible on mobile */
  .desktop-label {
    display: inline !important;
    color: #000000 !important;
  }

  /* Ensure form labels are black and visible on mobile - HIGHER SPECIFICITY */
  .ant-form-item-label > label,
  .tenant-edit-modal .ant-form-item-label > label,
  .ant-modal .ant-form-item-label > label,
  .tenant-form .ant-form-item-label > label,
  .tenant-edit-modal .tenant-form .ant-form-item-label > label {
    color: #000000 !important;

    @media (max-width: 768px) {
      color: #000000 !important;

      .anticon {
        color: var(--color-primary) !important;
      }

      .desktop-label {
        color: #000000 !important;
        display: inline !important;
      }
    }
  }

  /* Ensure label text spans are black */
  .ant-form-item-label > label span,
  .tenant-edit-modal .ant-form-item-label > label span,
  .ant-modal .ant-form-item-label > label span,
  .tenant-form .ant-form-item-label > label span,
  .tenant-edit-modal .tenant-form .ant-form-item-label > label span {
    color: #000000 !important;
  }

  /* Ensure icons in labels are visible */
  .ant-form-item-label > label .anticon,
  .tenant-edit-modal .ant-form-item-label > label .anticon,
  .ant-modal .ant-form-item-label > label .anticon,
  .tenant-form .ant-form-item-label > label .anticon,
  .tenant-edit-modal .tenant-form .ant-form-item-label > label .anticon {
    color: var(--color-primary) !important;
  }

  /* Ensure Modal form labels are black - EVEN HIGHER SPECIFICITY */
  .tenant-edit-modal .ant-form-item-label > label,
  .ant-modal.tenant-edit-modal .ant-form-item-label > label,
  .tenant-edit-modal .tenant-form .ant-form-item-label > label {
    color: #000000 !important;

    @media (max-width: 768px) {
      color: #000000 !important;

      &,
      * {
        color: #000000 !important;
      }

      span {
        color: #000000 !important;
      }

      .desktop-label {
        color: #000000 !important;
        display: inline !important;
      }

      .anticon {
        color: var(--color-primary) !important;
      }
    }

    span {
      color: #000000 !important;
    }

    .anticon {
      color: var(--color-primary) !important;
    }
  }

  /* Fix checkbox text wrapping on mobile */
  .ant-checkbox-wrapper {
    display: flex !important;
    align-items: flex-start !important;
    width: 100% !important;

    .ant-checkbox {
      flex-shrink: 0 !important;
      margin-top: 2px !important;
      width: auto !important;
      flex: 0 0 auto !important;
    }

    .ant-checkbox-inner {
      width: 16px !important;
      height: 16px !important;
      min-width: 16px !important;
      flex-shrink: 0 !important;
    }

    span {
      white-space: normal !important;
      word-wrap: break-word !important;
      line-height: 1.4 !important;
      flex: 1 !important;
      margin-left: 8px !important;
      min-width: 0 !important;
    }
  }

  /* Style checkbox: white background with black outline when unchecked */
  .ant-checkbox {
    .ant-checkbox-inner {
      background-color: #ffffff !important;
      border-color: #000000 !important;
      border-width: 2px !important;
      position: relative !important;
    }

    &:hover .ant-checkbox-inner {
      border-color: #000000 !important;
      background-color: #ffffff !important;
    }

    &.ant-checkbox-checked .ant-checkbox-inner {
      background-color: #000000 !important;
      border-color: #000000 !important;
    }

    &.ant-checkbox-checked:hover .ant-checkbox-inner {
      background-color: #000000 !important;
      border-color: #000000 !important;
    }

    .ant-checkbox-inner::after {
      border-color: #ffffff !important;
      /* Center the checkmark perfectly */
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) rotate(45deg) scale(0.8) !important;
      margin: 0 !important;
      width: 6px !important;
      height: 9px !important;
    }
  }

  /* Ensure Select dropdown arrows are vertically centered in modal */
  .ant-select {
    position: relative !important;

    .ant-select-selector {
      position: relative !important;
      display: flex !important;
      align-items: center !important;
      padding-right: 30px !important;
      min-height: 32px !important;
      height: 32px !important;
      max-height: 32px !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      display: flex !important;
      align-items: center !important;
      height: 100% !important;
      font-size: 14px !important;
    }

    .ant-select-arrow {
      position: absolute !important;
      right: 11px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin-top: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      margin-bottom: 0 !important;
      color: var(--color-text) !important;
      font-size: 12px !important;
      line-height: 1 !important;
      height: auto !important;
      width: auto !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      .anticon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
      }

      svg {
        display: inline-block !important;
      }
    }
  }

  .ant-select-single .ant-select-selector {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }

  /* Remove inner border from Input with prefix (like Monthly Rent) */
  .ant-input-affix-wrapper {
    /* Keep the outer border to match other inputs - use same border as globals.css */
    border: 2px solid #e2e8f0 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;

    &:hover {
      border-color: #40a9ff !important;
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }

    .ant-input {
      /* Remove inner border only */
      border: none !important;
      box-shadow: none !important;
      outline: none !important;

      &:focus,
      &:hover {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
      }
    }
  }
`;

const SettingsSection = styled.div`
  background: rgba(44, 95, 45, 0.04);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(44, 95, 45, 0.1);
  transition: all 0.2s ease;
  overflow-x: hidden !important;
  overflow-y: visible;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;

  /* Prevent any child from overflowing */
  * {
    max-width: 100%;
    box-sizing: border-box;
  }

  &:hover {
    background: rgba(44, 95, 45, 0.06);
    border-color: rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 1rem;
  }

  /* Responsive form item spacing */
  .ant-form-item {
    margin-bottom: 16px;
    max-width: 100%;
    overflow: hidden;

    @media (max-width: 768px) {
      margin-bottom: 12px;
    }
  }

  /* Ensure all rows don't overflow */
  .ant-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
    max-width: 100%;
    overflow-x: hidden;
  }

  /* Ensure all columns fit */
  .ant-col {
    max-width: 100% !important;
    overflow: hidden;
    padding-left: 0 !important;
    padding-right: 8px !important;
    box-sizing: border-box;
  }

  /* Rent Due Day horizontal layout on desktop */
  .rent-due-day-horizontal {
    @media (min-width: 768px) {
      .ant-form-item-label {
        text-align: left !important;
        padding-right: 16px !important;
      }

      .ant-form-item-control {
        flex: 1;
      }
    }
  }

  /* Select dropdown arrow vertical centering within settings sections */
  .ant-select {
    position: relative !important;

    .ant-select-selector {
      position: relative !important;
      display: flex !important;
      align-items: center !important;
      padding-right: 30px !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      display: flex !important;
      align-items: center !important;
      font-size: 14px !important;
    }

    .ant-select-arrow {
      position: absolute !important;
      right: 11px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin-top: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      margin-bottom: 0 !important;
      color: var(--color-text) !important;
      font-size: 12px !important;
      line-height: 1 !important;
      height: auto !important;
      width: auto !important;
      filter: brightness(1) invert(0) !important;
      pointer-events: none !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      &:hover {
        filter: brightness(1) invert(0) !important;
      }

      .anticon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
        vertical-align: middle !important;
      }

      svg {
        vertical-align: middle !important;
        display: inline-block !important;
      }
    }
  }

  /* Ensure the arrow container is properly sized */
  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    font-size: 14px !important;
    line-height: 1.5 !important;
    height: auto !important;
  }

  /* Responsive modal width for tablets and desktops */
  .tenant-edit-modal-responsive {
    @media (min-width: 768px) and (max-width: 1024px) {
      width: 700px !important;
      max-width: 95vw !important;
    }

    @media (min-width: 1025px) and (max-width: 1440px) {
      width: 750px !important;
      max-width: 90vw !important;
    }

    @media (min-width: 1441px) {
      width: 800px !important;
      max-width: 85vw !important;
    }
  }

  /* Prevent horizontal overflow */
  .tenant-edit-modal-responsive .ant-modal-body {
    overflow-x: hidden !important;
    max-width: 100% !important;
  }

  .tenant-edit-modal-responsive .tenant-form {
    overflow-x: hidden !important;
    max-width: 100% !important;
  }

  /* Ensure form fields don't overflow */
  .tenant-edit-modal-responsive .ant-col {
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .tenant-edit-modal-responsive .ant-form-item {
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .tenant-edit-modal-responsive .ant-input,
  .tenant-edit-modal-responsive .ant-input-number,
  .tenant-edit-modal-responsive .ant-select {
    max-width: 100% !important;
  }

  /* Ensure settings sections don't overflow */
  .tenant-edit-modal-responsive .ant-collapse-content-box {
    overflow-x: hidden !important;
    overflow-y: visible !important;
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .tenant-edit-modal-responsive .ant-collapse-item {
    overflow-x: hidden !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .tenant-edit-modal-responsive .ant-collapse {
    overflow-x: hidden !important;
    max-width: 100% !important;
  }

  /* Ensure ReminderFieldGroup doesn't overflow */
  .tenant-edit-modal-responsive .ReminderFieldGroup {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: hidden !important;
  }

  .tenant-edit-modal-responsive .ReminderRow {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: hidden !important;
  }

  /* Better spacing for form fields on tablets and desktops */
  .tenant-edit-modal-responsive .ant-row {
    @media (min-width: 768px) {
      margin-left: -8px !important;
      margin-right: -8px !important;
    }
  }

  .tenant-edit-modal-responsive .ant-col {
    @media (min-width: 768px) {
      padding-left: 8px !important;
      padding-right: 8px !important;
    }
  }

  /* Stack reminder fields vertically on all screen sizes */
  .tenant-edit-modal-responsive .ant-row .ant-col {
    margin-bottom: 0 !important;
  }

  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .ant-row {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .ant-col {
      padding-left: 8px !important;
      padding-right: 8px !important;
    }

    .ant-input-number,
    .ant-select,
    .ant-switch {
      width: 100% !important;
    }

    /* Stack remove button properly on mobile */
    .ant-form-item-label {
      padding-bottom: 4px;
    }

    /* Allow button text to wrap on mobile */
    .add-reminder-button,
    .add-notification-button {
      white-space: normal !important;
      height: auto !important;
      min-height: 40px !important;
      padding: 8px 16px !important;
      line-height: 1.4 !important;

      span {
        white-space: normal !important;
        display: inline-block !important;
        word-wrap: break-word !important;
        line-height: 1.4 !important;
      }
    }
  }

  /* Modal footer spacing - especially on mobile */
  .tenant-edit-modal .ant-modal-footer {
    padding-top: 1.5rem !important;
    padding-bottom: 0 !important;
    margin-top: 1rem !important;
    border-top: 1px solid var(--color-border-light) !important;

    @media (max-width: 768px) {
      padding-top: 2rem !important;
      padding-bottom: 0.25rem !important;
      margin-top: 1.5rem !important;
    }
  }

  .tenant-edit-modal .ant-modal-body {
    @media (max-width: 768px) {
      padding-bottom: 0.5rem !important;
    }
  }
`;

// Styled components for reminder rows
const ReminderRow = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (min-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const ReminderFieldGroup = styled.div`
  margin-bottom: 16px;

  /* Prevent Form.Item from changing height when Select is focused */
  .field-group .ant-form-item,
  .field-group .ant-form-item-has-feedback,
  .field-group .ant-form-item-has-success,
  .field-group .ant-form-item-has-warning,
  .field-group .ant-form-item-has-error,
  .field-group .ant-form-item-is-validating {
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    position: relative !important;
    top: 0 !important;
    transform: translateZ(0) !important;
    vertical-align: top !important;
  }

  /* Prevent validation icons from being added */
  .field-group .ant-form-item-has-feedback .ant-form-item-children-icon,
  .field-group .ant-form-item-has-success .ant-form-item-children-icon,
  .field-group .ant-form-item-has-warning .ant-form-item-children-icon,
  .field-group .ant-form-item-has-error .ant-form-item-children-icon {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .field-group .ant-form-item-control {
    margin: 0 !important;
    padding: 0 !important;
    position: relative !important;
    top: 0 !important;
    transform: translateZ(0) !important;
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }

  /* Prevent Form.Item from adding padding for validation icons */
  .field-group .ant-form-item-has-feedback .ant-form-item-control,
  .field-group .ant-form-item-has-success .ant-form-item-control,
  .field-group .ant-form-item-has-warning .ant-form-item-control,
  .field-group .ant-form-item-has-error .ant-form-item-control {
    padding-right: 0 !important;
  }

  .field-group .ant-form-item-control-input {
    margin: 0 !important;
    padding: 0 !important;
  }

  .field-group .ant-form-item-control-input-content {
    margin: 0 !important;
    padding: 0 !important;
    position: relative !important;
    top: 0 !important;
    transform: translateZ(0) !important;
  }

  /* InputNumber: vertically center the number in Days Before/After Due fields */
  .field-group .ant-input-number {
    display: flex !important;
    align-items: center !important;
  }

  .field-group .ant-input-number .ant-input-number-input {
    padding: 0 11px !important;
    line-height: 40px !important;
    height: 40px !important;
    text-align: left !important;
    box-sizing: border-box !important;
  }

  @media (min-width: 768px) {
    .field-group .ant-input-number .ant-input-number-input {
      line-height: 32px !important;
      height: 32px !important;
    }
  }

  /* Prevent any inline style changes from Ant Design - override inline styles */
  .field-group .ant-select[style*="margin"],
  .field-group .ant-select-focused[style*="margin"],
  .field-group .ant-select-open[style*="margin"],
  .field-group .ant-select[style*="top"],
  .field-group .ant-select-focused[style*="top"],
  .field-group .ant-select-open[style*="top"],
  .field-group .ant-select[style*="height"],
  .field-group .ant-select-focused[style*="height"],
  .field-group .ant-select-open[style*="height"] {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    top: 0 !important;
    transform: translateZ(0) !important;
  }

  /* Override inline height styles that Ant Design might apply - use attribute selector to catch any inline style */
  .field-group .ant-select-selector[style*="height"],
  .field-group .ant-select-focused .ant-select-selector[style*="height"],
  .field-group .ant-select-open .ant-select-selector[style*="height"],
  .field-group .ant-select-single .ant-select-selector[style*="height"],
  .field-group .ant-select-selector[style],
  .field-group .ant-select-focused .ant-select-selector[style],
  .field-group .ant-select-open .ant-select-selector[style],
  .field-group .ant-select-single .ant-select-selector[style] {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }

  /* Use CSS custom property to force height even with inline styles */
  .field-group .ant-select-selector {
    --forced-height: 32px !important;
  }

  /* Additional rule to catch any height changes - target the Select container itself */
  .field-group .ant-select,
  .field-group .ant-select-focused,
  .field-group .ant-select-open {
    height: auto !important; /* Let container be auto, but selector must be 32px */
  }

  .field-group .ant-select .ant-select-selector,
  .field-group .ant-select-focused .ant-select-selector,
  .field-group .ant-select-open .ant-select-selector {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    box-sizing: border-box !important;
  }

  /* Prevent Form.Item from adding margin when validation runs */
  .field-group .ant-form-item[style*="margin"],
  .field-group .ant-form-item-has-feedback[style*="margin"],
  .field-group .ant-form-item-has-success[style*="margin"],
  .field-group .ant-form-item-has-warning[style*="margin"],
  .field-group .ant-form-item-has-error[style*="margin"] {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  /* Lock the wrapper div that contains the Select */
  .field-group .ant-form-item-control-input-content > * {
    position: relative !important;
    top: 0 !important;
    left: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    transform: none !important;
    translate: none !important;
  }

  /* Lock the Select wrapper inside Form.Item control */
  .field-group .ant-form-item-control-input-content .ant-select {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  /* Hide validation feedback completely */
  .field-group .ant-form-item-explain,
  .field-group .ant-form-item-extra {
    display: none !important;
    height: 0 !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Lock Select component - prevent margin changes and all transitions */
  /* Target ALL possible states and combinations - apply to ALL selects proactively */
  .field-group .ant-select,
  .field-group .ant-select:not(.ant-select-focused):not(.ant-select-open),
  .field-group .ant-select-focused,
  .field-group .ant-select-open,
  .field-group .ant-select.ant-select-focused,
  .field-group .ant-select.ant-select-open,
  .field-group .ant-select-focused.ant-select-open,
  .field-group .ant-select[style*="margin"],
  .field-group .ant-select-focused[style*="margin"],
  .field-group .ant-select-open[style*="margin"] {
    margin: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    transition: none !important;
    animation: none !important;
    will-change: auto !important;
    position: relative !important;
    top: 0 !important;
    transform: translateZ(0) !important;
  }

  /* Prevent margin-top from changing on ANY state change */
  .field-group .ant-select[style*="margin-top"],
  .field-group .ant-select-focused[style*="margin-top"],
  .field-group .ant-select-open[style*="margin-top"] {
    margin-top: 0 !important;
  }

  /* Lock Form.Item margin-top to prevent shifts */
  .field-group .ant-form-item[style*="margin-top"],
  .field-group .ant-form-item-has-feedback[style*="margin-top"],
  .field-group .ant-form-item-has-success[style*="margin-top"],
  .field-group .ant-form-item-has-warning[style*="margin-top"],
  .field-group .ant-form-item-has-error[style*="margin-top"] {
    margin-top: 0 !important;
  }

  /* Disable all transitions on Select and its children */
  .field-group .ant-select *,
  .field-group .ant-select-focused *,
  .field-group .ant-select-open * {
    transition: none !important;
    animation: none !important;
  }

  /* Style disabled options - same size as regular options */
  .field-group .ant-select-dropdown .ant-select-item-option-disabled {
    opacity: 0.6 !important;
    color: #999 !important;
    cursor: not-allowed !important;
    background-color: #f5f5f5 !important;
    font-size: 14px !important;
  }

  .field-group .ant-select-dropdown .ant-select-item-option-disabled:hover {
    background-color: #f5f5f5 !important;
  }

  .field-group .ant-select-dropdown .ant-select-item-option-disabled .anticon {
    color: #999 !important;
  }

  .field-group .ant-select-dropdown .ant-select-item-option-disabled span {
    font-size: 14px !important;
  }

  /* Ensure ALL dropdown options have consistent font size - maximum specificity */
  /* Target every possible element to ensure 14px everywhere */
  .field-group .ant-select-dropdown .ant-select-item-option,
  .field-group .ant-select-dropdown .ant-select-item-option *,
  .field-group .ant-select-dropdown .ant-select-item-option-content,
  .field-group .ant-select-dropdown .ant-select-item-option-content *,
  .field-group
    .ant-select-dropdown
    .ant-select-item-option:not(.ant-select-item-option-disabled),
  .field-group
    .ant-select-dropdown
    .ant-select-item-option:not(.ant-select-item-option-disabled)
    *,
  .field-group
    .ant-select-dropdown
    .ant-select-item-option:not(.ant-select-item-option-disabled)
    .ant-select-item-option-content,
  .field-group
    .ant-select-dropdown
    .ant-select-item-option:not(.ant-select-item-option-disabled)
    .ant-select-item-option-content
    *,
  .field-group .ant-select-dropdown .ant-select-item-option span,
  .field-group .ant-select-dropdown .ant-select-item-option-content span,
  .field-group .ant-select-dropdown .ant-select-item-option-disabled,
  .field-group .ant-select-dropdown .ant-select-item-option-disabled *,
  .field-group .ant-select-dropdown .ant-select-item-option-disabled span,
  .field-group
    .ant-select-dropdown
    .ant-select-item-option-disabled
    .ant-select-item-option-content,
  .field-group
    .ant-select-dropdown
    .ant-select-item-option-disabled
    .ant-select-item-option-content
    span {
    font-size: 14px !important;
    line-height: 1.5 !important;
  }

  /* Ensure the selected value in the dropdown input also uses 14px - match dropdown exactly */
  .field-group .ant-select-selector .ant-select-selection-item,
  .field-group .ant-select-selector .ant-select-selection-item *,
  .field-group .ant-select-selector .ant-select-selection-placeholder,
  .field-group
    .ant-select-focused
    .ant-select-selector
    .ant-select-selection-item,
  .field-group
    .ant-select-focused
    .ant-select-selector
    .ant-select-selection-item
    *,
  .field-group .ant-select-open .ant-select-selector .ant-select-selection-item,
  .field-group
    .ant-select-open
    .ant-select-selector
    .ant-select-selection-item
    *,
  .field-group
    .ant-select-single
    .ant-select-selector
    .ant-select-selection-item,
  .field-group
    .ant-select-single
    .ant-select-selector
    .ant-select-selection-item
    *,
  .field-group
    .ant-select-single
    .ant-select-selector
    .ant-select-selection-placeholder {
    font-size: 14px !important;
    line-height: 1.5 !important;
  }

  /* Override any inline font-size styles with maximum specificity */
  .field-group
    .ant-select-selector
    .ant-select-selection-item[style*="font-size"],
  .field-group
    .ant-select-selector
    .ant-select-selection-placeholder[style*="font-size"],
  .field-group .ant-select-dropdown .ant-select-item-option[style*="font-size"],
  .field-group
    .ant-select-dropdown
    .ant-select-item-option
    span[style*="font-size"],
  .field-group
    .ant-select-dropdown
    .ant-select-item-option-disabled
    span[style*="font-size"],
  .field-group
    .ant-select-dropdown
    .ant-select-item-option-content[style*="font-size"] {
    font-size: 14px !important;
  }

  /* Prevent Form.Item from getting validation classes that change layout */
  .field-group .ant-form-item-has-feedback,
  .field-group .ant-form-item-has-success,
  .field-group .ant-form-item-has-warning,
  .field-group .ant-form-item-has-error {
    margin-bottom: 0 !important;
    margin-top: 0 !important;
  }

  /* Lock the selector - prevent margin/transform changes that cause movement */
  /* Target ALL possible class combinations that Ant Design might add */
  /* Override StyledModal's height: auto !important with more specific selector */
  /* Must be more specific than .ant-select-single .ant-select-selector in StyledModal */
  .field-group .ant-select-selector,
  .field-group .ant-select-focused .ant-select-selector,
  .field-group .ant-select-open .ant-select-selector,
  .field-group .ant-select.ant-select-focused .ant-select-selector,
  .field-group .ant-select.ant-select-open .ant-select-selector,
  .field-group .ant-select-focused.ant-select-open .ant-select-selector,
  .field-group .ant-select-single .ant-select-selector,
  .field-group .ant-select-single.ant-select-focused .ant-select-selector,
  .field-group .ant-select-single.ant-select-open .ant-select-selector,
  .tenant-edit-modal .field-group .ant-select-selector,
  .tenant-edit-modal .field-group .ant-select-focused .ant-select-selector,
  .tenant-edit-modal .field-group .ant-select-open .ant-select-selector,
  .tenant-edit-modal .field-group .ant-select-single .ant-select-selector,
  .tenant-edit-modal
    .field-group
    .ant-select-single.ant-select-focused
    .ant-select-selector,
  .tenant-edit-modal
    .field-group
    .ant-select-single.ant-select-open
    .ant-select-selector {
    margin: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    padding-left: 11px !important;
    padding-right: 11px !important;
    transform: translateZ(0) !important;
    transition: none !important;
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    box-sizing: border-box !important;
    vertical-align: top !important;
    position: relative !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  /* Lock the selection item inside - prevent line-height changes */
  .field-group .ant-select-selector .ant-select-selection-item,
  .field-group .ant-select-selector .ant-select-selection-placeholder,
  .field-group
    .ant-select-focused
    .ant-select-selector
    .ant-select-selection-item,
  .field-group .ant-select-open .ant-select-selector .ant-select-selection-item,
  .field-group
    .ant-select-single
    .ant-select-selector
    .ant-select-selection-item,
  .field-group
    .ant-select-single
    .ant-select-selector
    .ant-select-selection-placeholder {
    font-size: 14px !important;
    line-height: 1.5 !important;
    margin: 0 !important;
    padding: 0 !important;
    vertical-align: top !important;
  }

  /* Mobile divider between entries */
  @media (max-width: 767px) {
    &:not(:last-child) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding-bottom: 16px;
      margin-bottom: 16px;
    }
  }

  /* Mobile: Stack vertically with top row for enabled/remove */
  @media (max-width: 767px) {
    .reminder-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .mobile-top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }

    .field-group {
      display: flex;
      flex-direction: column;
    }

    .field-group label {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      color: rgba(0, 0, 0, 0.85);
      line-height: 1.2;
      text-align: left;
    }

    .field-group .ant-input-number,
    .field-group .ant-select {
      height: 40px;
      width: 100%;
    }

    .field-group .ant-select .ant-select-selector {
      height: 40px !important;
      padding: 8px 11px;
      text-align: left;
    }

    .field-group .ant-form-item {
      width: 100%;
      text-align: left;
      margin-bottom: 0;
    }

    .enabled-group {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .enabled-group label {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      color: rgba(0, 0, 0, 0.85);
      line-height: 1.2;
    }

    .remove-group {
      display: flex;
      align-items: flex-end;
    }

    /* Hide desktop enabled/remove elements on mobile */
    .reminder-row > .enabled-group,
    .reminder-row > .remove-group {
      display: none;
    }
  }

  /* Desktop/Tablet: Single line, vertically centered */
  @media (min-width: 768px) {
    .mobile-top-row {
      display: none;
    }

    .reminder-row {
      display: flex;
      align-items: flex-end;
      gap: 16px;
      min-height: 56px;
      max-height: 56px;
      height: 56px;
      position: relative;
      transform: translateZ(0);
      will-change: auto;
    }

    .reminder-row > * {
      position: relative;
      top: 0;
      transform: translateZ(0);
    }

    .field-group {
      display: flex;
      flex-direction: column;
      flex: 1;
      align-items: flex-start;
      height: 56px;
      min-height: 56px;
      max-height: 56px;
      position: relative;
      top: 0;
      left: 0;
      transform: translateZ(0);
      will-change: auto;
      margin: 0 !important;
      padding: 0 !important;
      vertical-align: top !important;
      align-self: flex-end;
    }

    .field-group .ant-form-item {
      align-self: flex-end;
    }

    .field-group label {
      font-size: 14px;
      font-weight: 500;
      margin: 0 0 4px 0 !important;
      margin-top: 0 !important;
      margin-bottom: 4px !important;
      padding: 0 !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      color: rgba(0, 0, 0, 0.85);
      line-height: 1.2;
      text-align: left;
      height: 20px;
      min-height: 20px;
      max-height: 20px;
      flex-shrink: 0;
      position: relative;
      top: 0;
    }

    .field-group .ant-input-number,
    .field-group .ant-select {
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      width: 100%;
      margin: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      padding: 0 !important;
      flex-shrink: 0;
      position: relative !important;
      top: 0 !important;
      transform: translateZ(0) !important;
    }

    .field-group .ant-select-focused,
    .field-group .ant-select-open {
      position: relative !important;
      top: 0 !important;
      transform: translateZ(0) !important;
      margin-top: 0 !important;
    }

    /* Prevent margin-top from changing via inline styles */
    .field-group .ant-select[style*="margin-top"],
    .field-group .ant-select-focused[style*="margin-top"],
    .field-group .ant-select-open[style*="margin-top"],
    .field-group .ant-form-item[style*="margin-top"] {
      margin-top: 0 !important;
    }

    .field-group .ant-select .ant-select-selector {
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      padding: 4px 11px !important;
      text-align: left;
      margin: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      box-sizing: border-box !important;
      border-width: 1px !important;
      border-style: solid !important;
      border-color: #d9d9d9 !important;
      box-shadow: none !important;
      outline: none !important;
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: translateZ(0) !important;
      translate: none !important;
      vertical-align: top !important;
      display: flex !important;
      align-items: center !important;
      font-size: 14px !important;
      line-height: 1.5 !important;
    }

    /* Prevent border/box-shadow changes on focus that cause layout shift */
    .field-group .ant-select-focused .ant-select-selector,
    .field-group .ant-select-open .ant-select-selector {
      border-width: 1px !important;
      box-shadow: none !important;
      outline: none !important;
    }

    /* Prevent any changes when focused or open - override StyledModal height: auto */
    .field-group .ant-select-focused .ant-select-selector,
    .field-group .ant-select-open .ant-select-selector,
    .field-group .ant-select-single .ant-select-selector {
      margin: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      padding-top: 4px !important;
      padding-bottom: 4px !important;
      transform: translateZ(0) !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      position: relative !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      box-sizing: border-box !important;
      vertical-align: top !important;
    }

    .field-group
      .ant-select-single
      .ant-select-selector
      .ant-select-selection-item,
    .field-group
      .ant-select-single
      .ant-select-selector
      .ant-select-selection-placeholder {
      font-size: 14px !important;
      line-height: 1.5 !important;
      margin: 0 !important;
      padding: 0 !important;
      vertical-align: top !important;
    }

    .field-group .ant-form-item {
      width: 100%;
      text-align: left;
      margin: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      padding: 0 !important;
      flex-shrink: 0;
      position: relative !important;
      top: 0 !important;
      transform: translateZ(0) !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
    }

    /* Prevent Form.Item margin-top changes on validation state */
    .field-group .ant-form-item-has-feedback,
    .field-group .ant-form-item-has-success,
    .field-group .ant-form-item-has-warning,
    .field-group .ant-form-item-has-error {
      margin-top: 0 !important;
    }

    .field-group .ant-form-item-control {
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
    }

    .field-group .ant-form-item-control-input {
      text-align: left;
      margin: 0 !important;
      padding: 0 !important;
      position: relative !important;
      top: 0 !important;
      transform: translateZ(0) !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
    }

    .field-group .ant-form-item-control-input-content {
      position: relative !important;
      top: 0 !important;
      transform: translateZ(0) !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
    }

    .enabled-group {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 80px;
      height: 56px;
      min-height: 56px;
      max-height: 56px;
      margin: 0 !important;
      padding: 0 !important;
      position: relative;
      top: 0;
    }

    .enabled-group label {
      font-size: 14px;
      font-weight: 500;
      margin: 0 0 4px 0 !important;
      padding: 0 !important;
      color: rgba(0, 0, 0, 0.85);
      line-height: 1.2;
      height: 20px;
      flex-shrink: 0;
    }

    .remove-group {
      display: flex;
      align-items: flex-end;
      min-width: 60px;
      height: 56px;
      min-height: 56px;
      max-height: 56px;
      margin: 0 !important;
      padding: 0 !important;
      position: relative;
      top: 0;
    }
  }
`;

const RemoveButton = styled(Button)`
  && {
    width: 36px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 0;
    background-color: transparent;
    border: none;
    font-size: 24px;
    font-weight: 900;
    color: #000000;
    line-height: 1;
    box-shadow: none;
    outline: none;
  }

  &&:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border: none;
    color: #000000;
    box-shadow: none;
    outline: none;
  }

  &&:focus {
    background-color: rgba(0, 0, 0, 0.05);
    border: none;
    color: #000000;
    box-shadow: none;
    outline: none;
  }

  &&:active {
    background-color: rgba(0, 0, 0, 0.1);
    border: none;
    color: #000000;
    box-shadow: none;
    outline: none;
  }

  /* Override any Ant Design button styles */
  &.ant-btn {
    background-color: transparent;
    border: none;
    color: #000000;
    font-size: 24px;
    font-weight: 900;
    box-shadow: none;
  }

  &.ant-btn:hover,
  &.ant-btn:focus,
  &.ant-btn:active {
    background-color: rgba(0, 0, 0, 0.05);
    border: none;
    color: #000000;
    box-shadow: none;
  }
`;
